import React, { useMemo, useState } from 'react';
import centralCallsAdapter from '../data/centralCallsAdapter';
import { validateReportPage } from '../core/modelGuards';
import { getGridDimensions, getWidgetGridStyle } from '../core/layout';
import ReportWidget from './ReportWidget';
import { registerDefaultWidgets } from '../widgets/registerDefaults';
import '../styles.css';

registerDefaultWidgets();

export default function ReportGrid({
  page,
  mode,
  editable,
  selectedWidgetId,
  onSelectWidget,
  onFilterValuesChange,
  onGridCellClick,
  onWidgetMoveStart,
  onWidgetResizeStart
}) {
  var [filterValuesByWidgetId, setFilterValuesByWidgetId] = useState({});

  var parsed = useMemo(function () {
    return validateReportPage(page || {});
  }, [page]);

  var widgets = parsed.page.widgets;

  var grid = useMemo(function () {
    var dims = getGridDimensions(widgets);
    return {
      columns: Math.max(dims.columns, 8),
      rows: Math.max(dims.rows, 8)
    };
  }, [widgets]);

  var canInteractWithWidgets = mode === 'edit' && editable === true;
  var canCreate = mode === 'edit' && editable === true;

  var allCells = useMemo(function () {
    var cells = [];
    for (var y = 1; y <= grid.rows; y += 1) {
      for (var x = 1; x <= grid.columns; x += 1) {
        cells.push({ x: x, y: y, id: String(x) + '-' + String(y) });
      }
    }
    return cells;
  }, [grid.rows, grid.columns]);

  var updateFilterValues = function updateFilterValues(widgetId, nextValue) {
    setFilterValuesByWidgetId(function (previous) {
      var next = {
        ...previous,
        [widgetId]: {
          ...(previous[widgetId] || {}),
          ...(nextValue || {})
        }
      };

      if (onFilterValuesChange) {
        onFilterValuesChange(next);
      }

      return next;
    });
  };

  if (!parsed.valid) {
    return (
      <div className="re-grid-error">
        <strong>Invalid report model:</strong> {parsed.errors.join(' ')}
      </div>
    );
  }

  var gridClassName = 're-grid';
  if (mode === 'edit') {
    gridClassName += ' re-grid-edit';
  }

  return (
    <div
      className={gridClassName}
      style={{
        gridTemplateColumns: 'repeat(' + String(grid.columns) + ', minmax(0, 1fr))',
        gridTemplateRows: 'repeat(' + String(grid.rows) + ', minmax(90px, auto))'
      }}
    >
      {canCreate ? (
        <div
          className="re-grid-overlay"
          style={{
            gridTemplateColumns: 'repeat(' + String(grid.columns) + ', minmax(0, 1fr))',
            gridTemplateRows: 'repeat(' + String(grid.rows) + ', minmax(90px, auto))'
          }}
        >
          {allCells.map(function (cell) {
            return (
              <button
                type="button"
                key={cell.id}
                className="re-grid-cell"
                style={{
                  gridColumn: String(cell.x) + ' / span 1',
                  gridRow: String(cell.y) + ' / span 1'
                }}
                onClick={function onClick(event) {
                  event.preventDefault();
                  event.stopPropagation();
                  if (onGridCellClick) {
                    onGridCellClick(cell);
                  }
                }}
                aria-label={'Grid cell ' + String(cell.x) + ',' + String(cell.y)}
              />
            );
          })}
        </div>
      ) : null}

      {widgets.map(function (widget) {
        var widgetInteractive = canInteractWithWidgets;

        return (
          <div
            key={widget.id}
            className={widgetInteractive ? 're-grid-item' : 're-grid-item re-grid-item-static'}
            style={getWidgetGridStyle(widget)}
          >
            <ReportWidget
              widget={widget}
              allWidgets={widgets}
              mode={mode}
              adapter={centralCallsAdapter}
              filterValuesByWidgetId={filterValuesByWidgetId}
              onFilterChange={updateFilterValues}
              isSelected={selectedWidgetId === widget.id}
              onSelect={onSelectWidget}
            />

            {widgetInteractive ? (
              <>
                <button
                  type="button"
                  className="re-widget-move-handle"
                  onMouseDown={function onMouseDown(event) {
                    event.preventDefault();
                    event.stopPropagation();
                    if (onWidgetMoveStart) {
                      onWidgetMoveStart(widget, event);
                    }
                  }}
                  aria-label="Move widget"
                >
                  Move
                </button>
                <button
                  type="button"
                  className="re-widget-resize-handle"
                  onMouseDown={function onMouseDown(event) {
                    event.preventDefault();
                    event.stopPropagation();
                    if (onWidgetResizeStart) {
                      onWidgetResizeStart(widget, event);
                    }
                  }}
                  aria-label="Resize widget"
                />
              </>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
